import React, { useContext, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Button } from '../../../../catalyst-components/button';
import { useNavigate } from 'react-router-dom';
import { Id } from '../../../../convex/_generated/dataModel';
import { BaseContext } from '../../../../shared/base-context';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import { Input } from '../../../../catalyst-components/input';
import { Field, Label } from '../../../../catalyst-components/fieldset';
import { Dialog, DialogBody, DialogTitle } from '../../../../catalyst-components/dialog';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../catalyst-components/alert';
import { ConfirmationDialog } from '../../../../catalyst-components/confirmation-dialog';
import { Select } from '../../../../catalyst-components/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../catalyst-components/accordion';
import { BadgeIcon } from '../../../../shared/components/BadgeIcon';
import { ICON_REGISTRY } from '../../../../shared/components/icon-registry';
import clsx from 'clsx';

export const LevelConfig: React.FC = () => {
  const navigate = useNavigate();
  const { convexUserId } = useContext(BaseContext);

  // Queries
  const overallLevels = useQuery(api.level_config.getOverallLevelConfigs) || [];
  const skills = useQuery(api.skills.list) || [];
  const allSkillLevelConfigs = useQuery(api.level_config.getAllSkillLevelConfigs) || [];

  // Mutations
  const upsertOverallLevel = useMutation(api.level_config.upsertOverallLevelConfig);
  const deleteOverallLevel = useMutation(api.level_config.deleteOverallLevelConfig);
  const upsertSkillLevel = useMutation(api.level_config.upsertSkillLevelConfig);
  const deleteSkillLevel = useMutation(api.level_config.deleteSkillLevelConfig);
  const seedDefaultOverallLevels = useMutation(api.level_config.seedDefaultOverallLevels);
  const seedDefaultSkillLevels = useMutation(api.level_config.seedDefaultSkillLevels);

  // State
  const [showOverallDialog, setShowOverallDialog] = useState(false);
  const [showSkillDialog, setShowSkillDialog] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState<Id<"skills"> | null>(null);
  const [editingOverall, setEditingOverall] = useState<any>(null);
  const [editingSkill, setEditingSkill] = useState<any>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; type: 'overall' | 'skill'; id?: string }>({ open: false, type: 'overall' });
  const [alert, setAlert] = useState<{ open: boolean; message: string; success: boolean }>({ open: false, message: '', success: false });

  // Form state for overall level
  const [overallForm, setOverallForm] = useState({
    level: 1,
    xp_required: 0,
    badge_name: '',
    badge_color: '#9CA3AF',
    badge_icon: '',
  });

  // Form state for skill level
  const [skillForm, setSkillForm] = useState({
    level: 1,
    xp_required: 0,
    badge_name: '',
    badge_color: '#9CA3AF',
    badge_icon: '',
  });

  // Group skill level configs by skill
  const skillLevelsBySkill = skills.map((skill) => ({
    skill,
    levels: allSkillLevelConfigs
      .filter((c) => c.skill_id === skill._id)
      .sort((a, b) => a.level - b.level),
  }));

  const handleSaveOverallLevel = async () => {
    try {
      await upsertOverallLevel({
        level: overallForm.level,
        xp_required: overallForm.xp_required,
        badge_name: overallForm.badge_name,
        badge_color: overallForm.badge_color,
        badge_icon: overallForm.badge_icon,
        created_by: convexUserId as Id<"users">,
      });
      setShowOverallDialog(false);
      setEditingOverall(null);
      setOverallForm({ level: 1, xp_required: 0, badge_name: '', badge_color: '#9CA3AF', badge_icon: '' });
      setAlert({ open: true, message: 'Overall level saved successfully!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error saving level. Please try again.', success: false });
    }
  };

  const handleSaveSkillLevel = async () => {
    if (!selectedSkillId) return;
    try {
      await upsertSkillLevel({
        skill_id: selectedSkillId,
        level: skillForm.level,
        xp_required: skillForm.xp_required,
        badge_name: skillForm.badge_name,
        badge_color: skillForm.badge_color,
        badge_icon: skillForm.badge_icon,
        created_by: convexUserId as Id<"users">,
      });
      setShowSkillDialog(false);
      setEditingSkill(null);
      setSkillForm({ level: 1, xp_required: 0, badge_name: '', badge_color: '#9CA3AF', badge_icon: '' });
      setAlert({ open: true, message: 'Skill level saved successfully!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error saving skill level. Please try again.', success: false });
    }
  };

  const handleDeleteOverall = async () => {
    if (!confirmDelete.id) return;
    try {
      await deleteOverallLevel({ id: confirmDelete.id as Id<"overall_level_config"> });
      setConfirmDelete({ open: false, type: 'overall' });
      setAlert({ open: true, message: 'Level deleted successfully!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error deleting level. Please try again.', success: false });
    }
  };

  const handleDeleteSkill = async () => {
    if (!confirmDelete.id) return;
    try {
      await deleteSkillLevel({ id: confirmDelete.id as Id<"skill_level_config"> });
      setConfirmDelete({ open: false, type: 'skill' });
      setAlert({ open: true, message: 'Skill level deleted successfully!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error deleting skill level. Please try again.', success: false });
    }
  };

  const handleSeedOverallLevels = async () => {
    try {
      await seedDefaultOverallLevels({ created_by: convexUserId as Id<"users"> });
      setAlert({ open: true, message: 'Default overall levels created!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error creating default levels.', success: false });
    }
  };

  const handleSeedSkillLevels = async (skillId: Id<"skills">) => {
    try {
      await seedDefaultSkillLevels({ skill_id: skillId, created_by: convexUserId as Id<"users"> });
      setAlert({ open: true, message: 'Default skill levels created!', success: true });
    } catch (error) {
      setAlert({ open: true, message: 'Error creating default skill levels.', success: false });
    }
  };

  const openEditOverall = (level: any) => {
    setEditingOverall(level);
    setOverallForm({
      level: level.level,
      xp_required: level.xp_required,
      badge_name: level.badge_name,
      badge_color: level.badge_color || '#9CA3AF',
      badge_icon: level.badge_icon || '',
    });
    setShowOverallDialog(true);
  };

  const openEditSkill = (skillId: Id<"skills">, level: any) => {
    setSelectedSkillId(skillId);
    setEditingSkill(level);
    setSkillForm({
      level: level.level,
      xp_required: level.xp_required,
      badge_name: level.badge_name || '',
      badge_color: level.badge_color || '#9CA3AF',
      badge_icon: level.badge_icon || '',
    });
    setShowSkillDialog(true);
  };

  const openAddSkillLevel = (skillId: Id<"skills">) => {
    setSelectedSkillId(skillId);
    setEditingSkill(null);
    const existingLevels = allSkillLevelConfigs.filter((c) => c.skill_id === skillId);
    const nextLevel = existingLevels.length > 0 ? Math.max(...existingLevels.map((l) => l.level)) + 1 : 1;
    setSkillForm({
      level: nextLevel,
      xp_required: 0,
      badge_name: '',
      badge_color: '#9CA3AF',
      badge_icon: '',
    });
    setShowSkillDialog(true);
  };

  return (
    <section className="min-h-screen bg-background pt-24 px-6 flex flex-col">
      <div className="flex flex-col justify-between mb-8">
        <h1 className="text-3xl font-bold">Points & Level Configuration</h1>
        <p className="text-gray-600 mt-2">
          Configure point thresholds and badges for overall user levels and per-skill levels.
        </p>
        <Button color="light" className="mt-4 mb-2 w-32" onClick={() => navigate('../')}>
          Go back
        </Button>
      </div>

      {/* Overall Level Configuration */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold">Overall Levels</h2>
          <div className="flex gap-2">
            {overallLevels.length === 0 && (
              <Button color="light" onClick={handleSeedOverallLevels}>
                Create Default Levels
              </Button>
            )}
            <Button
              color="light"
              onClick={() => {
                setEditingOverall(null);
                const nextLevel = overallLevels.length > 0 ? Math.max(...overallLevels.map((l) => l.level)) + 1 : 1;
                setOverallForm({ level: nextLevel, xp_required: 0, badge_name: '', badge_color: '#9CA3AF', badge_icon: '' });
                setShowOverallDialog(true);
              }}
            >
              Add Level
            </Button>
          </div>
        </div>

        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Level</TableHeader>
              <TableHeader>Points Required</TableHeader>
              <TableHeader>Badge Name</TableHeader>
              <TableHeader>Badge</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {overallLevels.map((level) => (
              <TableRow key={level._id}>
                <TableCell>{level.level}</TableCell>
                <TableCell>{level.xp_required.toLocaleString()}</TableCell>
                <TableCell>{level.badge_name}</TableCell>
                <TableCell>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-white text-sm"
                    style={{ backgroundColor: level.badge_color || '#9CA3AF' }}
                  >
                    {level.badge_icon && <BadgeIcon icon={level.badge_icon} className="h-4 w-4" />}
                    {level.badge_name}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button color="light" onClick={() => openEditOverall(level)}>
                      Edit
                    </Button>
                    <Button
                      color="red"
                      onClick={() => setConfirmDelete({ open: true, type: 'overall', id: level._id })}
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Skill Level Configuration */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-semibold mb-4">Skill Levels</h2>
        <p className="text-gray-600 mb-4">
          Configure point thresholds and badges for each skill. Users level up in skills separately from their overall level.
        </p>

        <Accordion type="single" collapsible className="w-full">
          {skillLevelsBySkill.map(({ skill, levels }) => (
            <AccordionItem key={skill._id} value={skill._id}>
              <AccordionTrigger className="text-xl font-semibold">
                {skill.name} ({levels.length} levels)
              </AccordionTrigger>
              <AccordionContent value={skill._id}>
                <div className="p-4 space-y-4">
                  <div className="flex justify-end gap-2 mb-4">
                    {levels.length === 0 && (
                      <Button color="light" onClick={() => handleSeedSkillLevels(skill._id)}>
                        Create Default Levels
                      </Button>
                    )}
                    <Button color="light" onClick={() => openAddSkillLevel(skill._id)}>
                      Add Level
                    </Button>
                  </div>

                  {levels.length > 0 ? (
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeader>Level</TableHeader>
                          <TableHeader>Points Required</TableHeader>
                          <TableHeader>Badge Name</TableHeader>
                          <TableHeader>Badge</TableHeader>
                          <TableHeader>Actions</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {levels.map((level) => (
                          <TableRow key={level._id}>
                            <TableCell>{level.level}</TableCell>
                            <TableCell>{level.xp_required.toLocaleString()}</TableCell>
                            <TableCell>{level.badge_name || `Level ${level.level}`}</TableCell>
                            <TableCell>
                              <span
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-white text-sm"
                                style={{ backgroundColor: level.badge_color || '#9CA3AF' }}
                              >
                                {level.badge_icon && <BadgeIcon icon={level.badge_icon} className="h-4 w-4" />}
                                {level.badge_name || `Level ${level.level}`}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button color="light" onClick={() => openEditSkill(skill._id, level)}>
                                  Edit
                                </Button>
                                <Button
                                  color="red"
                                  onClick={() => setConfirmDelete({ open: true, type: 'skill', id: level._id })}
                                >
                                  Delete
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      No levels configured for this skill yet. Click "Create Default Levels" to get started.
                    </p>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Overall Level Dialog */}
      <Dialog open={showOverallDialog} onClose={() => setShowOverallDialog(false)}>
        <DialogBody>
          <DialogTitle>{editingOverall ? 'Edit Overall Level' : 'Add Overall Level'}</DialogTitle>
          <div className="space-y-4 mt-4">
            <Field>
              <Label>Level Number</Label>
              <Input
                type="number"
                min="1"
                value={overallForm.level}
                onChange={(e) => setOverallForm({ ...overallForm, level: parseInt(e.target.value) || 1 })}
              />
            </Field>
            <Field>
              <Label>Points Required</Label>
              <Input
                type="number"
                min="0"
                value={overallForm.xp_required}
                onChange={(e) => setOverallForm({ ...overallForm, xp_required: parseInt(e.target.value) || 0 })}
              />
            </Field>
            <Field>
              <Label>Badge Name</Label>
              <Input
                value={overallForm.badge_name}
                onChange={(e) => setOverallForm({ ...overallForm, badge_name: e.target.value })}
                placeholder="e.g., Newcomer, Explorer, Master"
              />
            </Field>
            <Field>
              <Label>Badge Color</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="color"
                  value={overallForm.badge_color}
                  onChange={(e) => setOverallForm({ ...overallForm, badge_color: e.target.value })}
                  className="w-16 h-10"
                />
                <Input
                  value={overallForm.badge_color}
                  onChange={(e) => setOverallForm({ ...overallForm, badge_color: e.target.value })}
                  placeholder="#9CA3AF"
                />
              </div>
            </Field>
            <Field>
              <Label>Badge Icon</Label>
              <div className="grid grid-cols-6 gap-1.5 mt-1">
                {ICON_REGISTRY.map((entry) => (
                  <button
                    key={entry.key}
                    type="button"
                    onClick={() => setOverallForm({ ...overallForm, badge_icon: entry.key })}
                    className={clsx(
                      "flex flex-col items-center gap-1 p-2 rounded-lg border transition-all text-xs",
                      overallForm.badge_icon === entry.key
                        ? "border-primary bg-primary/10 ring-2 ring-primary"
                        : "border-border hover:border-primary/50 hover:bg-gray-50"
                    )}
                    title={entry.label}
                  >
                    <entry.component className="h-5 w-5" />
                    <span className="truncate w-full text-center">{entry.label}</span>
                  </button>
                ))}
              </div>
              <div className="mt-2">
                <Label className="text-xs text-muted-foreground">Or enter an emoji</Label>
                <Input
                  value={ICON_REGISTRY.some((e) => e.key === overallForm.badge_icon) ? '' : overallForm.badge_icon}
                  onChange={(e) => setOverallForm({ ...overallForm, badge_icon: e.target.value })}
                  placeholder="e.g., 🌱, ⭐, 🏆"
                  className="mt-1"
                />
              </div>
            </Field>
            <div className="mt-4">
              <span className="text-sm text-gray-600">Preview:</span>
              <span
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-white text-sm ml-2"
                style={{ backgroundColor: overallForm.badge_color || '#9CA3AF' }}
              >
                {overallForm.badge_icon && <BadgeIcon icon={overallForm.badge_icon} className="h-4 w-4" />}
                {overallForm.badge_name || `Level ${overallForm.level}`}
              </span>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button color="light" onClick={() => setShowOverallDialog(false)}>
              Cancel
            </Button>
            <Button color="light" onClick={handleSaveOverallLevel}>
              Save
            </Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* Skill Level Dialog */}
      <Dialog open={showSkillDialog} onClose={() => setShowSkillDialog(false)}>
        <DialogBody>
          <DialogTitle>{editingSkill ? 'Edit Skill Level' : 'Add Skill Level'}</DialogTitle>
          <div className="space-y-4 mt-4">
            <Field>
              <Label>Level Number</Label>
              <Input
                type="number"
                min="1"
                value={skillForm.level}
                onChange={(e) => setSkillForm({ ...skillForm, level: parseInt(e.target.value) || 1 })}
              />
            </Field>
            <Field>
              <Label>Points Required</Label>
              <Input
                type="number"
                min="0"
                value={skillForm.xp_required}
                onChange={(e) => setSkillForm({ ...skillForm, xp_required: parseInt(e.target.value) || 0 })}
              />
            </Field>
            <Field>
              <Label>Badge Name</Label>
              <Input
                value={skillForm.badge_name}
                onChange={(e) => setSkillForm({ ...skillForm, badge_name: e.target.value })}
                placeholder="e.g., Novice, Apprentice, Master"
              />
            </Field>
            <Field>
              <Label>Badge Color</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="color"
                  value={skillForm.badge_color}
                  onChange={(e) => setSkillForm({ ...skillForm, badge_color: e.target.value })}
                  className="w-16 h-10"
                />
                <Input
                  value={skillForm.badge_color}
                  onChange={(e) => setSkillForm({ ...skillForm, badge_color: e.target.value })}
                  placeholder="#9CA3AF"
                />
              </div>
            </Field>
            <Field>
              <Label>Badge Icon</Label>
              <div className="grid grid-cols-6 gap-1.5 mt-1">
                {ICON_REGISTRY.map((entry) => (
                  <button
                    key={entry.key}
                    type="button"
                    onClick={() => setSkillForm({ ...skillForm, badge_icon: entry.key })}
                    className={clsx(
                      "flex flex-col items-center gap-1 p-2 rounded-lg border transition-all text-xs",
                      skillForm.badge_icon === entry.key
                        ? "border-primary bg-primary/10 ring-2 ring-primary"
                        : "border-border hover:border-primary/50 hover:bg-gray-50"
                    )}
                    title={entry.label}
                  >
                    <entry.component className="h-5 w-5" />
                    <span className="truncate w-full text-center">{entry.label}</span>
                  </button>
                ))}
              </div>
              <div className="mt-2">
                <Label className="text-xs text-muted-foreground">Or enter an emoji</Label>
                <Input
                  value={ICON_REGISTRY.some((e) => e.key === skillForm.badge_icon) ? '' : skillForm.badge_icon}
                  onChange={(e) => setSkillForm({ ...skillForm, badge_icon: e.target.value })}
                  placeholder="e.g., 🌱, ⭐, 🏆"
                  className="mt-1"
                />
              </div>
            </Field>
            <div className="mt-4">
              <span className="text-sm text-gray-600">Preview:</span>
              <span
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-white text-sm ml-2"
                style={{ backgroundColor: skillForm.badge_color || '#9CA3AF' }}
              >
                {skillForm.badge_icon && <BadgeIcon icon={skillForm.badge_icon} className="h-4 w-4" />}
                {skillForm.badge_name || `Level ${skillForm.level}`}
              </span>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button color="light" onClick={() => setShowSkillDialog(false)}>
              Cancel
            </Button>
            <Button color="light" onClick={handleSaveSkillLevel}>
              Save
            </Button>
          </div>
        </DialogBody>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmationDialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, type: 'overall' })}
        onConfirm={confirmDelete.type === 'overall' ? handleDeleteOverall : handleDeleteSkill}
        title="Delete Level"
        description="Are you sure you want to delete this level? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isCritical={true}
      />

      {/* Alert */}
      <Alert open={alert.open} onClose={() => setAlert({ ...alert, open: false })} size="lg">
        <AlertTitle>{alert.success ? 'Success' : 'Error'}</AlertTitle>
        <AlertDescription>{alert.message}</AlertDescription>
        <AlertActions>
          <Button plain onClick={() => setAlert({ ...alert, open: false })}>
            OK
          </Button>
        </AlertActions>
      </Alert>
    </section>
  );
};
